// 의뢰서 상세 보철 목록 — 종류마다 한 줄 (대표 2026-11-02)
//   <지르코니아 크라운> #12
//   <PFM 크라운> #13
//   <임플란트 Osstem TS Mini> #14
//   <폰틱> #12
//   <브릿지> #12-#14, #15-#17
// 순서: 보철 종류(번호가 작은 치아부터 처음 나온 순) → 임플란트(브랜드별) → 폰틱 → 브릿지. 싱글 줄은 따로 없음(브릿지에 없는 치아 = 싱글).
// 순수 함수 — 화면(ViewOrder.js)과 시험(__tests__/orderKindLines.test.js)이 같이 쓴다. 저장 데이터는 바꾸지 않는다.

export const CHART_ORDER = [
    18, 17, 16, 15, 14, 13, 12, 11, 21, 22, 23, 24, 25, 26, 27, 28,
    48, 47, 46, 45, 44, 43, 42, 41, 31, 32, 33, 34, 35, 36, 37, 38,
];
const rank = (t) => { const i = CHART_ORDER.indexOf(t); return i === -1 ? 999 + t : i; };
const sortChart = (teeth) => [...new Set(teeth)].sort((a, b) => rank(a) - rank(b));
const teethOf = (g) => {
    const t = g && g.teeth;
    if (Array.isArray(t)) return t.map(Number).filter((n) => Number.isFinite(n));
    if (typeof t === 'string') return t.split(/[-,]/).map((x) => Number(x.trim())).filter((n) => Number.isFinite(n) && n > 0);
    return [];
};
// 치아 나열은 번호 오름차순(대표 예시 #12-#14 처럼): 11→18, 21→28, 31→38, 41→48
const sortNum = (teeth) => [...new Set(teeth)].sort((a, b) => a - b);
const list = (teeth) => sortNum(teeth).map((t) => `#${t}`).join(', ');

/** 보철 이름 — 표준 명칭('지르코니아 크라운')은 재료가 이름에 들어 있음. 옛 분해형('Crown'+'지르코니아')만 재료를 앞에 붙인다 */
const nameOf = (prosthesis, material) => {
    const p = String(prosthesis || '').trim();
    const m = String(material || '').trim();
    if (!p || p === '-') return m && m !== '-' ? m : '';
    if (!m || m === '-' || p.includes(m)) return p;
    return `${m} ${p}`;
};

/** 브릿지 한 묶음 → '#12-#14'(작은 번호-큰 번호). 정중선을 넘으면(예 12·11·21·22) 치식 순서 끝끼리 '#12-#22' */
const bridgeText = (teeth) => {
    const s = sortChart(teeth);
    if (!s.length) return '';
    if (s.length === 1) return `#${s[0]}`;
    const quads = new Set(s.map((t) => Math.floor(t / 10)));
    if (quads.size > 1) return `#${s[0]}-#${s[s.length - 1]}`;
    const n = sortNum(s);
    return `#${n[0]}-#${n[n.length - 1]}`;
};

/**
 * @param {object} order 의뢰서 문서(prosthesisGroups·implantGroups·ponticGroups·bridgeGroups·items)
 * @returns {{label: string, teeth: string}[]}
 */
export function orderKindLines(order) {
    if (!order) return [];
    const items = Array.isArray(order.items) ? order.items : [];

    // 1) 보철 종류 — 치아별 하나(prosthesisGroups 우선, 없으면 items)
    const prosByTooth = {};
    (order.prosthesisGroups || []).forEach((g) => {
        const name = nameOf(g.prosthesis, g.material);
        if (name) teethOf(g).forEach((t) => { prosByTooth[t] = name; });
    });
    items.forEach((it) => {
        const t = Number(it.toothNumber);
        if (!Number.isFinite(t) || prosByTooth[t]) return;
        const name = nameOf(it.prosthesis, it.material);
        if (name) prosByTooth[t] = name;
    });
    const prosOrder = [];
    const prosTeeth = {};
    sortNum(Object.keys(prosByTooth).map(Number)).forEach((t) => {
        const n = prosByTooth[t];
        if (!prosTeeth[n]) { prosTeeth[n] = []; prosOrder.push(n); }
        prosTeeth[n].push(t);
    });

    // 2) 임플란트 — 브랜드별(implantGroups 우선, items 보완)
    const impByTooth = {};
    (order.implantGroups || []).forEach((g) => { const b = String(g.brand || '').trim(); if (b) teethOf(g).forEach((t) => { impByTooth[t] = b; }); });
    items.forEach((it) => {
        const t = Number(it.toothNumber); const b = String(it.implant || '').trim();
        if (Number.isFinite(t) && b && b !== '없음' && !impByTooth[t]) impByTooth[t] = b;
    });
    const impOrder = [];
    const impTeeth = {};
    sortNum(Object.keys(impByTooth).map(Number)).forEach((t) => {
        const b = impByTooth[t];
        if (!impTeeth[b]) { impTeeth[b] = []; impOrder.push(b); }
        impTeeth[b].push(t);
    });

    // 3) 폰틱
    const pon = [];
    (order.ponticGroups || []).forEach((g) => pon.push(...teethOf(g)));
    items.forEach((it) => { if (it.isPontic && Number.isFinite(Number(it.toothNumber))) pon.push(Number(it.toothNumber)); });

    // 4) 브릿지
    const bridges = (order.bridgeGroups || []).map(teethOf).filter((b) => b.length >= 2)
        .sort((a, b) => Math.min(...a) - Math.min(...b));

    const out = [];
    prosOrder.forEach((n) => out.push({ label: n, teeth: list(prosTeeth[n]) }));
    impOrder.forEach((b) => out.push({ label: `임플란트 ${b}`, teeth: list(impTeeth[b]) }));
    if (pon.length) out.push({ label: '폰틱', teeth: list(pon) });
    if (bridges.length) out.push({ label: '브릿지', teeth: [...new Set(bridges.map(bridgeText))].join(', ') });
    return out;
}
